// ExtractionData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const extraction: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Tooth Extraction Dubai",
            "description": "Pain-free tooth extraction services in Dubai by expert dental team at Elite Body Home. Advanced techniques for decay, overcrowding or wisdom teeth complications.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Tooth Extraction Dubai"],
        "hero": {
            "title": "Tooth Extraction in Dubai",
            "subtitle": "Pain-Free Tooth Extraction for a Brighter Smile",
            "description": "Our pain-free and easy tooth extraction services are provided by our expert dental team at Elite Body Home, Dubai, to focus on your comfort. Whether you're struggling with decay, overcrowding, or wisdom teeth complications, our advanced techniques ensure a seamless experience and minimal downtime. Our compassionate team is here to help you attain a healthier smile in a cutting-edge dental facility you can trust. Call now and take the next step to healthy teeth!"
        },
        "howItWorks": {
            "title": "Pointwise Steps for Tooth Extraction Procedure",
            "description": "A Tooth Extraction is a dental procedure used in the removal of a tooth that is damaged, decayed, or is causing pain. The procedure can be considered simple, or surgical, based on the nature of the situation."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Local Anesthesia",
                    "desc": "Local anaesthetic is used to numb the area surrounding the tooth to make the procedure as painless as possible.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Tooth Loosening",
                    "desc": "The dentist will apply pressure around the tooth with dental instruments to loosen the tooth from its socket.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Simple Extraction vs. Surgical Extraction",
                    "desc": "Visible teeth are removed in a simple extraction, while incisions might be required in a surgical extraction for impacted or broken teeth.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "Post-Extraction Care",
                    "desc": "Your dentist will explain how to manage pain, bleeding, and healing during your recovery process from the procedure.",
                    "icon": "fa-solid fa-bandage"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "There are many reasons tooth extraction can be beneficial, especially if it is a severely damaged or impacted tooth.",
            "items": [
                {
                    "title": "Pain Relief",
                    "desc": "Extracting an affected tooth offers instant relief, minimizing the chances of future complications.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Preventing Infection Spread",
                    "desc": "Infected teeth can spread infection to the rest of the mouth or even enter the bloodstream, so extraction can prevent that from happening.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "Avoiding Overcrowding",
                    "desc": "Tooth extraction is also required in orthodontic cases where tooth alignment and bite improve when space is created in the mouth",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Resolving Impaction Issues",
                    "desc": "Wisdom teeth are commonly the last molars to emerge, Usually, they need to be removed and are considered impacted if they cannot emerge properly and can also cause misalignment as well as infections or damage to other teeth.",
                    "icon": "fa-solid fa-tooth"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "While not everybody needs tooth extraction, there are many instances where the procedure is advised.",
            "items": [
                {
                    "title": "Severe Tooth Decay",
                    "desc": "If a tooth is too damaged for a filling or a crown, it may need to be extracted.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Periodontal disease in its advanced state",
                    "desc": "With severe gum disease, extracting teeth may be necessary to protect the surrounding teeth and bone.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Impacted Wisdom Teeth",
                    "desc": "Wisdom teeth that don't emerge properly can lead to pain, infection or other dental issues.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Orthodontic Treatment",
                    "desc": "Orthodontic treatment may call for one or more teeth to be extracted for proper alignment.",
                    "icon": "fa-solid fa-braces"
                },
                {
                    "title": "Fractured or Broken Teeth",
                    "desc": "If a tooth cannot be restored using methods such as bonding, crowns or other means, extraction is the best option.",
                    "icon": "fa-solid fa-teeth"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "Top 5 Ways Preparing For a Tooth Extraction Could Help You",
            "para": "There are several things you can do to prepare before your appointment.",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "A thorough examination X-rays of the tooth and surrounding area will be performed by your dentist.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "Medication Review",
                    "desc": "Let your dentist know about any medicines or supplements that you are taking, as this could impact the extraction or healing process.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Pre-Procedure Instructions",
                    "desc": "Your dentist will provide specific directions to you, including whether you need to fast before the procedure if sedation is being used.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Arrange Transportation",
                    "desc": "If you'll be sedated or placed under general anesthesia, plan to have someone drive you home afterward; the medication may affect your ability to drive.",
                    "icon": "fa-solid fa-car"
                },
                {
                    "title": "Plan for Aftercare",
                    "desc": "Stock up on soft foods, prepare pain management supplies, and review post-operative care instructions, to aid in healing.",
                    "icon": "fa-solid fa-home"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Analyses of lasting results generally reveal favorable results for tooth extraction, particularly if carried out to relieve discomfort or reduce the risk of future dental complications. What to expect after the procedure:",
            "items": [
                {
                    "title": "Immediate Pain Relief",
                    "desc": "Patients typically feel instant relief once the tooth that is causing the problem is extracted.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Primitively Care To Actually Sooner Heal",
                    "desc": "If you follow your dentist's aftercare recommendations — avoiding strenuous exertion, for instance, and eating soft foods — your recovery should be quick and uncomplicated.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Improved Oral Health",
                    "desc": "Extracting a compromised or affected tooth can eliminate the risk of infections, crowding, or other mouth problems potentially impacting your overall oral health.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Enhanced Quality of Life",
                    "desc": "Patients frequently say they feel much better and more comfortable long-term, able to chew, speak and feel at ease, and in some cases their jaws are helped to open with treatment too.",
                    "icon": "fa-solid fa-thumbs-up"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "FAQs",
            "subheading": "Tooth extractions are often surrounded by questions about the procedure and recovery. Below are common FAQs to help ease any concerns you may have.",
            "items": [
                {
                    "question": "Is tooth extraction painful?",
                    "answer": "Local anesthesia or sedation ensures the procedure is painless. You may experience mild discomfort afterward, but it is manageable with over-the-counter pain medications."
                },
                {
                    "question": "How long does it take to recover from a tooth extraction?",
                    "answer": "Most patients recover within 1-2 weeks. For surgical extractions, recovery may take a bit longer, especially for impacted wisdom teeth."
                },
                {
                    "question": "What can I eat after a tooth extraction?",
                    "answer": "Soft foods such as yogurt, mashed potatoes, and smoothies are recommended for the first few days. Avoid chewing near the extraction site to promote healing."
                },
                {
                    "question": "Can I brush my teeth after an extraction?",
                    "answer": "You can brush your teeth, but avoid the extraction site for the first 24 hours to prevent dislodging the blood clot. After 24 hours, gently rinse your mouth with salt water."
                }
            ]
        },
        "treatmentAreas1": [

        ],
        "treatmentAreas2": [

        ]
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "خلع الأسنان في دبي",
            "description": "خدمات خلع الأسنان بدون ألم في دبي بواسطة فريق أسنان خبرة في عيادات ايليت بودي هوم. تقنيات متقدمة لعلاج التسوس، تزاحم الأسنان أو مشاكل ضروس العقل.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "خلع الأسنان في دبي"],
        "hero": {
            "title": "خلع الأسنان في دبي",
            "subtitle": "خلع الأسنان بدون ألم لابتسامة أكثر إشراقًا",
            "description": "نقدم في ايليت بودي هوم بدبي خدمات خلع الأسنان بطريقة سهلة وخالية من الألم على يد فريق طبي من الخبراء، مع تركيز كامل على راحتك. سواء كنت تعاني من تسوس، أو تزاحم في الأسنان، أو مشاكل في ضروس العقل، نستخدم أحدث التقنيات لضمان تجربة سلسة ووقت تعافٍ قصير. سيساعدك فريقنا على الوصول إلى ابتسامة صحية داخل عيادتنا المتطورة التي يمكنك الوثوق بها. اتصل الآن واتخذ الخطوة التالية نحو أسنان أكثر صحة!"
        },
        "howItWorks": {
            "title": "خطوات إجراء خلع الأسنان",
            "description": "خلع السن هو إجراء طبي يُستخدم لإزالة السن التالف، أو المتآكل، أو المسبب للألم، ويُصنف الإجراء إما كخلع بسيط أو خلع جراحي، وذلك حسب حالة السن."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "التخدير الموضعي",
                    "desc": "يُستخدم مخدر موضعي لتخدير المنطقة المحيطة بالسن، مما يجعل الإجراء غير مؤلم قدر الإمكان.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تحريك السن",
                    "desc": "يقوم طبيب الأسنان بالضغط بلطف حول السن بهدف تفكيكه من اللثة المحيطة به.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "الخلع البسيط والخلع الجراحي",
                    "desc": "يُستخدم الخلع البسيط عندما يكون السن ظاهرًا ويسهل الوصول إليه، بينما يتطلب الخلع الجراحي شقوقًا جراحية لإزالة الأسنان المطمرة أو المكسورة.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "العناية بعد الخلع",
                    "desc": "سيقدم لك طبيب الأسنان إرشادات شاملة حول كيفية السيطرة على الألم، وتقليل النزيف، وتعزيز التئام المنطقة خلال فترة التعافي.",
                    "icon": "fa-solid fa-bandage"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "يوجد العديد من الأسباب التي تجعل خلع الأسنان مفيدًا، خاصةً في حال وجود سن تالف بشدة أو مطمر داخل اللثة.",
            "items": [
                {
                    "title": "تخفيف الآلام",
                    "desc": "يساعد خلع السن المصاب على تخفيف الألم فورًا، ويقلل من احتمالية حدوث مضاعفات لاحقة.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "الحد من انتشار العدوى",
                    "desc": "يمكن للأسنان المصابة أن تنقل العدوى إلى بقية الفم أو حتى إلى مجرى الدم، ويمنع خلعها حدوث هذه المضاعفات.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "تجنب تزاحم الأسنان",
                    "desc": "يُستخدم خلع الأسنان في بعض حالات تقويم الأسنان لتحسين اصطفاف الأسنان وإطباق الفكين من خلال توفير مساحة إضافية داخل الفم.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "علاج الأسنان المطمرة",
                    "desc": "عادةً ما تكون ضروس العقل هي آخر الأسنان ظهورًا، وقد لا تظهر بشكل كامل مما يؤدي إلى انطمارها. في هذه الحالات، يُوصى بخلعها لتجنب الالتهابات، أو التزاحم، أو تضرر الأسنان المجاورة.",
                    "icon": "fa-solid fa-tooth"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "لا تحتاج كل المرضى إلى خلع الأسنان، ولكن توجد بعض الحالات يُوصى فيها بهذا الإجراء.",
            "items": [
                {
                    "title": "تسوس الأسنان الشديد",
                    "desc": "تتعرض بعض الأسنان لتلف كبير ولا يمكن إصلاحه بحشو الأسنان أو بالتيجان، قد يكون الخلع هو الخيار الأنسب.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "أمراض اللثة المتقدمة",
                    "desc": "قد يؤدي تراجع العظم الداعم للأسنان إلى ضرورة خلع السن في حالات التهاب اللثة المزمن، للحفاظ على الأسنان المجاورة والعظام.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "ضروس العقل المطمرة",
                    "desc": "قد تسبب ضروس العقل ألمًا أو التهابات أو مشكلات في اصطفاف الأسنان إذا لم تنمو بشكل سليم، مما قد يستدعي خلعها.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "العلاج التقويمي",
                    "desc": "يتطلب العلاج التقويمي خلع بعض الأسنان أحيانًا، لتوفير مساحة تساعد على ترتيب الأسنان بشكل صحيح.",
                    "icon": "fa-solid fa-braces"
                },
                {
                    "title": "الأسنان المكسورة أو المتشققة",
                    "desc": "يكون الخلع هو الحل الأمثل في حال كانت كسور الأسنان شديدة ولا يمكن ترميمها باستخدام الحشوات أو التيجان.",
                    "icon": "fa-solid fa-teeth"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "أفضل 5 خطوات للاستعداد لخلع الأسنان التي قد تساعدك",
            "para": "للتحضير قبل موعدك. إليك أهم ما يجب فعله قبل الموعد:",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "سيقوم طبيب الأسنان بإجراء فحص شامل يتضمن أشعة سينية لتقييم حالة السن والمنطقة المحيطة به.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "مراجعة الأدوية",
                    "desc": "أخبر طبيبك بأي أدوية أو مكملات غذائية تتناولها، فقد تؤثر على التخدير أو على التئام الجرح.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "التعليمات المسبقة للإجراء",
                    "desc": "إذا كنت ستخضع لتخدير، قد يُطلب منك الصيام لعدة ساعات قبل العملية. سيشرح لك الطبيب كل التعليمات اللازمة مسبقاً.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "ترتيب وسيلة نقل",
                    "desc": "في حال استخدام التخدير، يُفضل أن يرافقك شخص لتوصيلك إلى المنزل بعد الإجراء، لأنك قد لا تكون قادراً على القيادة.",
                    "icon": "fa-solid fa-car"
                },
                {
                    "title": "الاستعداد لفترة ما بعد الخلع",
                    "desc": "قم بتجهيز الأطعمة اللينة، ومسكنات الألم الموصوفة، واطلع على تعليمات العناية بعد الخلع لتسهيل عملية الشفاء.",
                    "icon": "fa-solid fa-home"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "تشير التحليلات إلى أن خلع الأسنان غالباً ما يُحقق نتائج إيجابية، خاصة عند إجرائه لتخفيف الألم أو لتجنب المضاعفات المستقبلية في الفم. ما يمكن توقعه بعد الإجراء:",
            "items": [
                {
                    "title": "تخفيف فوري للألم",
                    "desc": "يشعر المريض براحة فورية بمجرد إزالة السن المتضرر أو المسبب للألم.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "شفاء أسرع مع العناية المناسبة",
                    "desc": "يساعد اتباع تعليمات الطبيب بعد الخلع، مثل الراحة وتناول الأطعمة اللينة، في تسريع عملية الشفاء وتقليل المضاعفات.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "تحسن صحة الفم",
                    "desc": "يساهم إزالة السن المصاب أو التالف في الوقاية من الالتهابات، وتزاحم الأسنان، ومشكلات أخرى قد تؤثر على صحة الفم العامة.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "تحسن جودة الحياة",
                    "desc": "يشعر كثير من المرضى بتحسن كبير بعد الخلع، إذ يصبح المضغ والتحدث أكثر راحة، وقد يشعر البعض بتحسن في حركة الفك خاصة في حال وجود ضغط أو تزاحم سابق.",
                    "icon": "fa-solid fa-thumbs-up"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "عادة ما يكون خلع الأسنان محاطًا بالعديد من الأسئلة حول الإجراء والتعافي. إليك بعض الأسئلة الشائعة لمساعدتك في التخفيف من أي مخاوف لديك.",
            "items": [
                {
                    "question": "هل خلع الأسنان مؤلم؟",
                    "answer": "يضمن التخدير الموضعي أن يكون الإجراء غير مؤلم. قد تشعر بعدم ارتياح بسيط بعد ذلك، لكن يمكن التحكم فيه باستخدام مسكنات الألم المتاحة دون وصفة طبية."
                },
                {
                    "question": "كم من الوقت يستغرق التعافي من خلع الأسنان؟",
                    "answer": "يتعافى معظم المرضى في غضون أسبوع إلى أسبوعين. قد يستغرق التعافي وقتًا أطول في حالات الخلع الجراحي، خاصةً لضروس العقل المطمرة."
                },
                {
                    "question": "ماذا يمكنني أن آكل بعد خلع الأسنان؟",
                    "answer": "يُنصح بتناول الأطعمة اللينة مثل الزبادي، البطاطس المهروسة، والعصائر في الأيام القليلة الأولى. تجنب المضغ بالقرب من مكان الخلع لتعزيز الشفاء."
                },
                {
                    "question": "هل يمكنني تنظيف أسناني بعد الخلع؟",
                    "answer": "يمكنك تنظيف أسنانك، لكن تجنب منطقة الخلع خلال الـ24 ساعة الأولى لمنع إزاحة جلطة الدم. بعد 24 ساعة، يمكنك المضمضة بلطف بماء مالح."
                }
            ]
        },
        "treatmentAreas1": [
        ],
        "treatmentAreas2": [

        ]
    },
    shared: {
        images: {
            introductionImage: "",
            beforeAfter: [],
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bdgranner-Image-scaled.jpg"
        },
        product: {
            id: 0,
            title: "Tooth Extraction",
            price: 499,
            description: ""
        }
    }
};
